import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { CheckCircle, User, CreditCard, Briefcase } from 'lucide-react';
import { visaProgramAPI } from '../../utils/api';

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'card', label: 'Credit / Debit Card' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'online', label: 'Online Payment' },
];

export default function RegisterLeadModal({ lead, open, onClose, onConfirm }) {
  const [programs, setPrograms] = useState([]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    payment_amount: '',
    payment_mode: 'cash',
    payment_reference: '',
    payment_date: new Date().toISOString().slice(0, 10),
    visa_process_enrolled: '',
    visa_program_id: '',
    generate_portal: true,
    send_welcome_email: true,
  });
  const [confirming, setConfirming] = useState(false);
  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  useEffect(() => {
    visaProgramAPI.getPrograms()
      .then(({ data }) => setPrograms(Array.isArray(data) ? data : []))
      .catch(() => {});
    if (lead) {
      set('payment_amount', lead.quoted_price || '');
    }
  }, [lead]);

  const handleConfirm = async () => {
    if (!form.payment_amount) { return; }
    setConfirming(true);
    try {
      await onConfirm({
        payment_amount: parseFloat(form.payment_amount),
        payment_mode: form.payment_mode,
        payment_reference: form.payment_reference,
        payment_date: form.payment_date,
        visa_process_enrolled: form.visa_process_enrolled,
        visa_program_id: form.visa_program_id,
        generate_portal: form.generate_portal,
        send_welcome_email: form.send_welcome_email,
      });
    } finally {
      setConfirming(false);
    }
  };

  const steps = [
    { n: 1, label: 'Payment', icon: CreditCard },
    { n: 2, label: 'Enrollment', icon: Briefcase },
    { n: 3, label: 'Confirm', icon: CheckCircle },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-teal-600" />
            Register as Client — {lead?.full_name}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 py-2">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const active = step === s.n;
            const done = step > s.n;
            return (
              <React.Fragment key={s.n}>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  done ? 'bg-teal-100 text-teal-700' :
                  active ? 'bg-indigo-100 text-indigo-700' :
                  'bg-slate-100 text-slate-400'
                }`}>
                  <Icon className="w-3.5 h-3.5" />
                  {s.label}
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-px ${step > s.n ? 'bg-teal-300' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 1: Payment */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Quoted Price:</span>
                <span className="font-semibold">INR {Number(lead?.quoted_price || 0).toLocaleString()}</span>
              </div>
              {lead?.discount && (
                <div className="flex justify-between text-emerald-600">
                  <span>Discount:</span>
                  <span>- INR {Number(lead.discount).toLocaleString()}</span>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Amount Received (INR) *</Label>
              <Input
                type="number"
                value={form.payment_amount}
                onChange={(e) => set('payment_amount', e.target.value)}
                placeholder="Enter amount"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Payment Mode</Label>
              <Select value={form.payment_mode} onValueChange={(v) => set('payment_mode', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Reference No.</Label>
                <Input value={form.payment_reference} onChange={(e) => set('payment_reference', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Payment Date</Label>
                <Input type="date" value={form.payment_date} onChange={(e) => set('payment_date', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Enrollment */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Visa Process / Program Enrolled</Label>
              <Select
                value={form.visa_program_id}
                onValueChange={(v) => {
                  set('visa_program_id', v);
                  const prog = programs.find((p) => p.id === v);
                  if (prog) set('visa_process_enrolled', prog.name);
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select a visa program" /></SelectTrigger>
                <SelectContent>
                  {programs.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} — {p.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3 bg-slate-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Generate Portal Credentials</p>
                  <p className="text-xs text-slate-500">Create a client portal login for this client</p>
                </div>
                <Switch
                  checked={form.generate_portal}
                  onCheckedChange={(v) => set('generate_portal', v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Send Welcome Email</p>
                  <p className="text-xs text-slate-500">Email login details to the client</p>
                </div>
                <Switch
                  checked={form.send_welcome_email}
                  onCheckedChange={(v) => set('send_welcome_email', v)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 space-y-2 text-sm">
              <h3 className="font-semibold text-teal-800 flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4" /> Review & Confirm
              </h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-slate-700">
                <div className="text-slate-500">Client Name:</div>
                <div className="font-medium">{lead?.full_name}</div>
                <div className="text-slate-500">Amount Paid:</div>
                <div className="font-semibold text-emerald-700">INR {Number(form.payment_amount).toLocaleString()}</div>
                <div className="text-slate-500">Payment Mode:</div>
                <div>{PAYMENT_MODES.find((m) => m.value === form.payment_mode)?.label}</div>
                {form.payment_reference && (
                  <>
                    <div className="text-slate-500">Reference:</div>
                    <div>{form.payment_reference}</div>
                  </>
                )}
                {form.visa_process_enrolled && (
                  <>
                    <div className="text-slate-500">Program:</div>
                    <div>{form.visa_process_enrolled}</div>
                  </>
                )}
              </div>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-700 space-y-1">
              <p className="font-semibold">What happens next:</p>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                <li>Client profile created in the system</li>
                <li>Sales record auto-created in Accounting</li>
                {form.generate_portal && <li>Portal login credentials generated</li>}
                {form.send_welcome_email && <li>Welcome email sent to client</li>}
                <li>Lead marked as Registered Client</li>
              </ul>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep((s) => s - 1)} disabled={confirming}>
              Back
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={confirming}>Cancel</Button>
          {step < 3 ? (
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 1 && !form.payment_amount}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Next →
            </Button>
          ) : (
            <Button
              onClick={handleConfirm}
              disabled={confirming}
              className="bg-teal-600 hover:bg-teal-700 gap-1.5"
            >
              <CheckCircle className="w-4 h-4" />
              {confirming ? 'Registering...' : 'Register Client'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
